<div class="space-y-4">
  <!-- Loading -->
  @if (loading()) {
    <div class="card card-padding text-sm text-slate-500" data-testid="loading">
      Loading conflict...
    </div>
  }

  <!-- Load error / PI-only notice -->
  @if (errorMsg(); as msg) {
    <div class="card card-padding space-y-3" data-testid="load-error">
      <p class="text-sm text-red-600">{{ msg }}</p>
      <button class="btn-secondary" (click)="back()" data-testid="error-back-btn">
        Back to conflicts
      </button>
    </div>
  }

  @if (detail(); as d) {
    @if (d.readOnly) {
      <div
        class="card card-padding bg-slate-50 text-sm text-slate-600"
        data-testid="readonly-notice"
      >
        This project is completed. Conflicts are shown read-only.
      </div>
    }

    <!-- Paper metadata card -->
    <div class="card card-padding">
      <div class="flex items-start justify-between gap-4">
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-slate-800 text-base">{{ d.paper.title }}</h3>
          <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            @if (d.paper.publicationDate) {
              <span class="text-xs text-slate-500">{{
                d.paper.publicationDate | date: 'yyyy'
              }}</span>
            }
            @if (d.paper.authors) {
              <span class="text-xs text-slate-500">{{ d.paper.authors }}</span>
            }
            @if (d.paper.doi) {
              <span class="text-xs text-slate-500">DOI: {{ d.paper.doi }}</span>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- PDF load error -->
    @if (pdfLoadError(); as perr) {
      <div class="card card-padding text-sm text-red-600" data-testid="pdf-error">{{ perr }}</div>
    }

    <!-- Questions card -->
    <div class="card card-padding space-y-4">
      @for (q of d.questions; track q.questionId) {
        <div
          class="rounded-lg border border-slate-200 p-4 space-y-3"
          [attr.data-testid]="'question-' + q.questionId"
        >
          <!-- Header: question text + type / Required badges (no Conflict / Evidence-required) -->
          <div class="flex flex-wrap items-start gap-2">
            <span class="flex-1 min-w-0 text-base font-semibold text-slate-800 break-words">{{
              q.text
            }}</span>
            <div class="flex flex-wrap items-center gap-1.5 shrink-0">
              <span class="badge-neutral">{{ typeLabel(q.type) }}</span>
              @if (q.required) {
                <span class="badge-danger" title="Required">Required</span>
              }
              @if (q.conflict) {
                <span class="badge-primary" title="Conflict">Conflict</span>
              }
            </div>
          </div>

          @if (q.conflict) {
            <!-- Two read-only reviewer columns -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  {{ d.reviewerA.reviewerName }}
                </p>
                <app-data-extraction-questionnaire
                  [questions]="[asQuestion(q)]"
                  [answers]="answerRow(q.questionId, q.reviewerAValue)"
                  [readOnly]="true"
                  [hideHeader]="true"
                ></app-data-extraction-questionnaire>
              </div>
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  {{ d.reviewerB.reviewerName }}
                </p>
                <app-data-extraction-questionnaire
                  [questions]="[asQuestion(q)]"
                  [answers]="answerRow(q.questionId, q.reviewerBValue)"
                  [readOnly]="true"
                  [hideHeader]="true"
                ></app-data-extraction-questionnaire>
              </div>
            </div>

            @if (!d.readOnly) {
              <!-- PI decision controls -->
              <div class="flex flex-wrap gap-2">
                <button
                  class="btn-secondary"
                  [class.ring-2]="choiceFor(q.questionId) === 'acceptA'"
                  (click)="acceptA(q)"
                  [attr.data-testid]="'accept-a-btn'"
                >
                  Accept {{ d.reviewerA.reviewerName }}'s answer
                </button>
                <button
                  class="btn-secondary"
                  [class.ring-2]="choiceFor(q.questionId) === 'acceptB'"
                  (click)="acceptB(q)"
                  [attr.data-testid]="'accept-b-btn'"
                >
                  Accept {{ d.reviewerB.reviewerName }}'s answer
                </button>
                <button
                  class="btn-secondary"
                  [class.ring-2]="choiceFor(q.questionId) === 'neither'"
                  (click)="neither(q)"
                  [attr.data-testid]="'neither-btn'"
                >
                  Neither, choose answer
                </button>
              </div>

              @if (isNeither(q.questionId)) {
                <div
                  class="rounded-md bg-slate-50 p-3 space-y-2"
                  [attr.data-testid]="'neither-widget-' + q.questionId"
                >
                  <app-data-extraction-questionnaire
                    [questions]="[asQuestion(q)]"
                    [answers]="answerRow(q.questionId, stagedValue(q.questionId))"
                    [hideHeader]="true"
                    (answerChange)="onNeither(q, $event.value)"
                  ></app-data-extraction-questionnaire>
                  @if (neitherError(q.questionId); as nerr) {
                    <p
                      class="text-xs text-red-600"
                      [attr.data-testid]="'neither-error-' + q.questionId"
                    >
                      {{ nerr }}
                    </p>
                  }
                </div>
              }
            }
          } @else {
            <!-- Agreed / non-conflicted: single read-only widget with the agreed value -->
            <app-data-extraction-questionnaire
              [questions]="[asQuestion(q)]"
              [answers]="answerRow(q.questionId, q.reviewerAValue)"
              [readOnly]="true"
              [hideHeader]="true"
            ></app-data-extraction-questionnaire>
          }

          @let ocrA = ocrTextLines(d.reviewerA.reviewerId, q.questionId);
          @let ocrB = ocrTextLines(d.reviewerB.reviewerId, q.questionId);
          @if (ocrA.length > 0 || ocrB.length > 0) {
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                  OCR Text — {{ d.reviewerA.reviewerName }}
                </p>
                @for (line of ocrA; track line) {
                  <p class="text-xs text-slate-600 break-words whitespace-normal">{{ line }}</p>
                }
                @if (ocrA.length === 0) {
                  <p class="text-xs text-slate-400 italic">No OCR text</p>
                }
              </div>
              <div>
                <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                  OCR Text — {{ d.reviewerB.reviewerName }}
                </p>
                @for (line of ocrB; track line) {
                  <p class="text-xs text-slate-600 break-words whitespace-normal">{{ line }}</p>
                }
                @if (ocrB.length === 0) {
                  <p class="text-xs text-slate-400 italic">No OCR text</p>
                }
              </div>
            </div>
          }

          <!-- View annotations -->
          <div class="mt-2">
            <button
              class="btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              [disabled]="pdfLoading()"
              (click)="viewAnnotations(q)"
              [attr.data-testid]="'view-annotations-' + q.questionId"
            >
              @if (pdfLoading()) {
                <span
                  class="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin inline-block"
                ></span>
                Loading...
              } @else {
                <svg
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View Annotations
              }
            </button>
          </div>
        </div>
      }
    </div>

    <!-- Resolve error summary banner -->
    @if (resolveError(); as rerr) {
      <div class="card card-padding text-sm text-red-600" data-testid="resolve-error">
        {{ rerr }}
      </div>
    }

    <!-- Footer -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <button class="btn-secondary" (click)="back()" data-testid="back-btn">
          Back to conflicts
        </button>
        @if (touched() && !d.readOnly) {
          <span class="text-xs text-amber-600" data-testid="unsaved-indicator">
            Unsaved changes
          </span>
        }
      </div>

      @if (!d.readOnly) {
        <div class="flex items-center gap-3">
          @if (blockedReason(); as reason) {
            <span class="text-xs text-slate-500" data-testid="blocked-reason">{{ reason }}</span>
          }
          <button
            [class.btn-primary]="canResolve() && !resolving()"
            [class.btn-disabled-primary]="!canResolve() || resolving()"
            [disabled]="!canResolve() || resolving()"
            (click)="resolve()"
            data-testid="resolve-btn"
          >
            {{ resolving() ? 'Resolving...' : 'Resolve' }}
          </button>
        </div>
      }
    </div>

    <!-- Reviewer annotations modal -->
    @if (modalPdf(); as pdf) {
      <app-reviewer-annotations-modal
        [open]="modalOpen()"
        (openChange)="onModalOpenChange($event)"
        [projectId]="projectIdForModal"
        [paperId]="d.paper.paperId"
        [documentId]="modalDocumentId()"
        [pdf]="pdf"
        [reviewerA]="d.reviewerA"
        [reviewerB]="d.reviewerB"
        [claimFilter]="modalClaimFilter()"
        [questionText]="modalQuestion()?.text ?? ''"
        [claims]="claims()"
      ></app-reviewer-annotations-modal>
    }
  }
</div>
